import { apiRequest, ApiResponse } from '../request'
import { Chat, MessageItem } from '../common/interfaces/message.interface'

export interface ChatWithItems extends Chat {
    messages: MessageItem[]
    aiOnHold: boolean
    /** Início do período de atendimento atual; a conversa é reusada e dividida em períodos. */
    currentPeriodStartedAt?: string | null
    company: {
        id: number
        name: string
        tradingName: string
        whatsappNumber: string
        city?: string | null
        state?: string | null
    } | null
    currentDepartment?: {
        id: number
        name: string
        whatsappNumber?: string | null
    } | null
    // true quando a janela de 24h do WhatsApp está aberta (cliente respondeu
    // nas últimas ~24h). Determina se o atendimento pode ser reaberto direto.
    whatsappWindowOpen?: boolean
    // false quando a IA está globalmente desativada (kill-switch openai.enabled
    // do painel master). Nesse caso o botão de pausar/retomar IA fica desabilitado.
    aiEnabled?: boolean
    // Instruções da equipe para a IA valerem só nesta conversa.
    aiInstructions?: string | null
    aiInstructionsMode?: ChatAiInstructionsMode | null
    aiInstructionsExpiresAt?: string | null
    aiInstructionsUpdatedAt?: string | null
    aiInstructionsUserName?: string | null
}

/** Até quando valem as instruções da equipe para a IA numa conversa. */
export type ChatAiInstructionsMode = 'NEXT_REPLY' | 'UNTIL_TIME' | 'PERMANENT'

// Unidade (company) da organização, para definir a unidade de um chat em
// triagem (companyId null).
export interface OrganizationUnit {
    id: number
    name: string
    tradingName?: string | null
    city?: string | null
    state?: string | null
    neighborhood?: string | null
}

export async function getOrganizationUnits(): Promise<
    ApiResponse<OrganizationUnit[]>
> {
    return apiRequest<OrganizationUnit[]>('chat/organization-units', {}, 'get')
}

/**
 * Quem está vendo viu a conversa: zera a não lida DELE. Os colegas seguem com a deles.
 */
export async function markChatRead(chatId: number): Promise<ApiResponse<{ id: number; unreadCount: number }>> {
    return apiRequest(`chat/${chatId}/read`, {}, 'patch')
}

/**
 * Evento local disparado quando a leitura de quem está vendo muda (abriu a conversa ou
 * marcou como não lida). A leitura é por pessoa e o backend não avisa a empresa toda,
 * então é este evento que faz a lista e o "Visto por" desta tela se atualizarem.
 */
export const CHAT_READ_CHANGED_EVENT = 'chat-read-changed'

export interface ChatReader {
    userId: number
    name: string
    lastReadAt: string
}

/** "Visto por": quem da equipe abriu ou respondeu a conversa, e quando. */
export async function getChatReaders(
    chatId: number,
): Promise<ApiResponse<{ lastInboundAt: string | null; readers: ChatReader[] }>> {
    return apiRequest(`chat/${chatId}/readers`, {}, 'get')
}

/** Devolve a conversa às pendentes de quem marcou, com a contagem recalculada. */
export async function markChatUnread(chatId: number): Promise<ApiResponse<{ id: number; unreadCount: number }>> {
    return apiRequest(`chat/${chatId}/unread`, {}, 'patch')
}

export async function assignChatUnit(
    chatId: number,
    companyId: number
): Promise<ApiResponse<{ id: number; companyId: number; company: OrganizationUnit }>> {
    return apiRequest(`chat/${chatId}/unit`, { data: { companyId } }, 'patch')
}

export async function getChatByCompany(
    companyId: number,
    params?: Record<string, any>
): Promise<ApiResponse<Chat[]>> {
    return apiRequest<Chat[]>(`chat/company/${companyId}`, { params: params || {} }, 'get')
}

export async function getChatWithMessages(
    id: number
): Promise<ApiResponse<ChatWithItems>> {
    return apiRequest<ChatWithItems>(`chat/${id}/chat`, {}, 'get')
}

export interface SendManualMessageData {
    messageId: number
    body?: string
    mediaUrl?: string[]
    /** Mensagem citada. Só produz balão de resposta no canal WhatsApp Web. */
    replyToMessageId?: number
}

export async function sendManualMessage(
    data: SendManualMessageData
): Promise<ApiResponse<any>> {
    return apiRequest<any>('chat/send', { data }, 'post')
}

export interface SendManualTemplateData {
    messageId: number
    /** Legado: identificador do provedor anterior. Use `templateId`. */
    contentSid?: string
    /** Id local — identifica o template em todos os canais. */
    templateId?: number
    variables?: string[]
    previewText?: string
}

export async function sendManualTemplate(
    data: SendManualTemplateData
): Promise<ApiResponse<any>> {
    return apiRequest<any>('chat/send-template', { data }, 'post')
}

export interface StartChatWithTemplateData {
    phoneNumber: string
    name: string
    departmentId: number
    /** Legado: identificador do provedor anterior. Use `templateId`. */
    contentSid?: string
    /** Id local — obrigatório no canal WHATSAPP_API (janela de 24h). */
    templateId?: number
    /** Texto livre — só aceito no canal WHATSAPP_WEB, que não tem janela. */
    body?: string
    variables?: string[]
    previewText?: string
}

export interface StartChatWithTemplateResult {
    success: boolean
    chatId: number
    created: boolean
    reused: boolean
    clientLinked: boolean
}

export async function startChatWithTemplate(
    data: StartChatWithTemplateData
): Promise<ApiResponse<StartChatWithTemplateResult>> {
    return apiRequest<StartChatWithTemplateResult>('chat/start-with-template', { data }, 'post')
}

export async function uploadMedia(files: File[]): Promise<
    ApiResponse<{
        success: boolean
        files: Array<{
            filename: string
            originalname: string
            url: string
            mimetype: string
            size: number
        }>
    }>
> {
    const formData = new FormData()
    files.forEach(file => {
        formData.append('media', file)
    })

    return apiRequest<{
        success: boolean
        files: Array<{
            filename: string
            originalname: string
            url: string
            mimetype: string
            size: number
        }>
    }>('chat/upload-media', { data: formData, formData: true }, 'post')
}

export async function closeChat(id: number): Promise<ApiResponse<Chat>> {
    return apiRequest<Chat>(`chat/${id}/close`, {}, 'patch')
}

export async function toggleAiOnHold(id: number): Promise<ApiResponse<Chat>> {
    return apiRequest<Chat>(`chat/${id}/toggle-ai-hold`, {}, 'patch')
}

export async function setChatAiInstructions(
    id: number,
    data: { instructions: string; mode: ChatAiInstructionsMode; hours?: number }
): Promise<ApiResponse<Chat>> {
    return apiRequest<Chat>(`chat/${id}/ai-instructions`, { data }, 'patch')
}

export async function clearChatAiInstructions(id: number): Promise<ApiResponse<Chat>> {
    return apiRequest<Chat>(`chat/${id}/ai-instructions`, {}, 'delete')
}

export async function reopenChat(id: number): Promise<ApiResponse<Chat>> {
    return apiRequest<Chat>(`chat/${id}/reopen`, {}, 'patch')
}

export async function moveChatDepartment(
    id: number,
    departmentId: number
): Promise<ApiResponse<Chat>> {
    return apiRequest<Chat>(
        `chat/${id}/department`,
        { data: { departmentId } },
        'patch'
    )
}

export async function markHumanAttended(
    id: number
): Promise<ApiResponse<Chat>> {
    return apiRequest<Chat>(`chat/${id}/mark-human-attended`, {}, 'patch')
}

export interface WaitingForHumanChat {
    id: number
    phoneNumber: string
    name: string
    updatedAt: string
    reason: string
}

export async function getWaitingForHuman(): Promise<
    ApiResponse<WaitingForHumanChat[]>
> {
    return apiRequest<WaitingForHumanChat[]>(
        'chat/waiting-for-human',
        {},
        'get'
    )
}

export async function getChatsByClient(
    clientId: number,
    companyId?: number
): Promise<ApiResponse<Chat[]>> {
    const params = companyId ? { companyId } : {}
    return apiRequest<Chat[]>(`chat/client/${clientId}`, { params }, 'get')
}

// Um atendimento: o chat é reusado para o mesmo cliente/número, então acumula
// vários períodos. O resumo é opcional — período curto não gera texto, mas o
// atendimento é registrado do mesmo jeito.
export interface ChatPeriod {
    id: number
    chatId: number
    summary?: string | null
    summaryFull?: string | null
    periodStart: string
    periodEnd: string
    messageCount: number
    /** A IA respondeu neste atendimento. É o que consome cota do plano. */
    aiParticipated: boolean
    aiMessageCount: number
    createdAt: string
}

export async function getChatPeriods(
    id: number
): Promise<ApiResponse<ChatPeriod[]>> {
    return apiRequest<ChatPeriod[]>(`chat/${id}/periods`, {}, 'get')
}

/** Cliente que casa o telefone da conversa (telefone é 1:N). */
export interface ChatClientCandidate {
    id: number
    name: string
    cpf?: string | null
    birthDate?: string | null
    isProspect: boolean
}

export async function getChatClientCandidates(chatId: number): Promise<
    ApiResponse<{
        chatId: number
        phoneNumber: string
        contactName: string
        linkedClientId: number | null
        candidates: ChatClientCandidate[]
    }>
> {
    return apiRequest(`chat/${chatId}/client-candidates`, {}, 'get')
}

/**
 * Telefone do contato de uma conversa do Instagram/Messenger. O backend liga a
 * conversa ao cliente pelo telefone e junta o lead do canal ao que já existia.
 */
export async function setChatContactPhone(
    chatId: number,
    phoneNumber: string
): Promise<
    ApiResponse<{
        chatId: number
        phoneNumber: string
        clientId: number | null
        leadId: number | null
    }>
> {
    return apiRequest(
        `chat/${chatId}/contact-phone`,
        { data: { phoneNumber } },
        'patch'
    )
}

export async function linkChatClient(
    chatId: number,
    clientId: number
): Promise<ApiResponse<{ chatId: number; clientId: number }>> {
    return apiRequest(`chat/${chatId}/client`, { data: { clientId } }, 'patch')
}

/**
 * Apaga a mensagem no WhatsApp do contato e a remove do atendimento.
 * Só funciona em setores no canal WhatsApp Web.
 */
export async function deleteChatMessage(
    messageId: number
): Promise<ApiResponse<{ success: boolean; revoked: boolean }>> {
    return apiRequest<{ success: boolean; revoked: boolean }>(
        `chat/messages/${messageId}`,
        {},
        'delete'
    )
}

export interface MyDepartment {
    id: number
    name: string
}

/**
 * Setores de que o usuário é membro — os únicos cujos atendimentos ele vê.
 * Lista vazia significa que ele só enxerga a triagem (chats sem setor).
 */
export async function getMyDepartments(): Promise<ApiResponse<MyDepartment[]>> {
    return apiRequest<MyDepartment[]>('chat/my-departments', {}, 'get')
}
